import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../redux/modules/snackbar';
import { profileActionCreators } from '../../../redux/modules/profile';
import history from '../../../utils/history';
import { getAppointmentById, patchAppointment } from '../../../services/appointment/appointment.service';
import { OPENTOK_APIKEY } from '../../../constants/CommonConstants';
import { getCareTeamMember } from '../../../services/member/member.service';
import { getUserTimeZone } from '../../../utils/dayjs';
import { getProfile } from '../../../redux/modules/profile/selectors';
import { getAuth } from '../../../redux/modules/auth/selectors';
import dayjs from 'dayjs';
import VideoCallScreen from './video-call-screen';
import EndSession from './end-session';
import FeedbackAppointment from '../appointments/components/feedback-appointment';
import { PatchAction } from '../../../redux/modules/appointment/types';
import { ProvideEvaluationHead } from '../../../hooks/useEvaluationHead';
import { colors, Icons } from '../../../packages';

const Sessions = () => {
  const { appointmentId } = useParams();
  const dispatch = useDispatch();
  const { demographicDetails } = useSelector(getProfile);
  const { meta } = useSelector(getAuth);

  const [appointment, setAppointment] = useState(null);
  const [isEndedSession, setIsEndedSession] = useState(false);
  const [showEndSessionConfirm, setShowEndSessionConfirm] = useState(false);
  const [telesessionToken, setTelesessionToken] = useState('');
  const [telesessionId, setTelesessionId] = useState('');
  const [isWaiting, setIsWaiting] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showFeedbackAppointment, setShowFeedbackAppointment] = useState(false);
  const [careTeamList, setCareTeamList] = useState([]);
  const [session, setSession] = useState(null);
  const [callEndedReason, setCallEndedReason] = useState(null);
  const [inPersonSession, setInPersonSession] = useState(false);

  useEffect(() => {
    if (!appointment && appointmentId) {
      getAppointmentById({ appointmentId })
        .then(res => {
          const item = res.data;
          dispatch(profileActionCreators.fetchDemographicDetails(item.participantId));
          const newItem = {
            ...item,
            participantGenderIdentity: demographicDetails?.genderIdentity,
          };

          setAppointment(newItem);
          dispatch(profileActionCreators.fetchDomainTypes(item.participantId));
          getCareTeamList(item);

          if (item?.inPersonSession) {
            setIsWaiting(false);
            setInPersonSession(true);
          }

          joinAppointmentRequest(appointmentId);
        })
        .catch(e => {
          console.log(e);
          const message =
            e?.data?.errors?.[0]?.endUserMessage ||
            e?.errors?.[0]?.endUserMessage ||
            e?.message ||
            'Something went wrong. please try again';
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: message,
            }),
          );
        });
    }
  }, [appointment, appointmentId]);

  /**
   * @Name useEffect
   * @description This method is used to get Evaluation List with progress detail of each evaluation
   */
  useEffect(() => {
    if (!appointmentId) {
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Please provide appointment Id',
        }),
      );
    }
  }, []);

  /**
   * @Name onEndSession
   * @description This method is used to end tele session
   * Note: The session-ended signal is now sent from StreamContainer before this is called
   */
  const onEndSession = (endedVia = null) => {
    // Signal is now sent from VideoCallScreen/StreamContainer, just call API
    patchAppointment({ action: PatchAction.COMPLETE_APPOINTMENT }, { appointmentId })
      .then(response => {
        if (response.status === 200) {
          setShowEndSessionConfirm(false);
          setIsEndedSession(true);
          setShowFeedbackAppointment(true);
          setIsWaiting(false);
        }
      })
      .catch(err => {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: 'Whoops ! something went wrong please try again',
          }),
        );
        setShowEndSessionConfirm(false);
        console.log('Whoops ! something went wrong please try again', err);
      });
  };

  /**
   * @Name toggleEndSessionConfirm
   * @description This method is used to show end session popup and navigate to next screen
   */
  const toggleEndSessionConfirm = (endedVia = null) => {
    if (!isEndedSession) {
      setShowEndSessionConfirm(s => !s);
      setCallEndedReason(endedVia);
    }
  };

  /**
   * @Name handleMemberEndedSession
   * @description Called when member ends the session (via OpenTok signal)
   */
  const handleMemberEndedSession = () => {
    setIsEndedSession(true);
    setIsWaiting(false);
  };

  const startInPersonCall = () => {
    setIsWaiting(false);
    setInPersonSession(true);
    void patchAppointment({ action: PatchAction.START_IN_PERSON }, { appointmentId });
  };

  const onCloseFeedbackAppointment = (isSubmitted = false) => {
    setShowFeedbackAppointment(false);
  };

  /**
   * @Name joinAppointmentRequest
   * @description This method is used to get session details like session token etc
   */
  const joinAppointmentRequest = useCallback(id => {
    patchAppointment({ action: PatchAction.ARRIVE_FOR_APPOINTMENT }, { appointmentId: id })
      .then(({ data }) => {
        setTelesessionToken(data?.telesessionToken);
        setTelesessionId(data?.telesessionId);
        setSession(OT.initSession(OPENTOK_APIKEY, data?.telesessionId));
        setIsInitializing(false);
      })
      .catch(err => {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: err.data?.errors?.[0]?.endUserMessage || 'Can not join appointment',
          }),
        );
        history.push(
          `/provider/${meta?.userId}/day-schedule?date=${dayjs().tz(getUserTimeZone()).format('YYYY-MM-DD')}`,
        );
      });
  }, []);

  /**
   * @Name getCareTeamList
   * @description This method is used to get care team list for the patient
   */
  const getCareTeamList = appt => {
    if (!appt?.participantId) {
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Please provide Member Id',
        }),
      );
    } else {
      getCareTeamMember({ userId: appt?.participantId }, { careNavigator: true })
        .then(careTeamListResponse => {
          if (careTeamListResponse?.status !== 200) {
            dispatch(
              showSnackbar({
                snackType: 'error',
                snackMessage: 'Whoops ! Something went wrong . Please try later',
              }),
            );
          } else {
            setCareTeamList(careTeamListResponse?.data || []);
          }
        })
        .catch(error => {
          console.log('Whoops ! Something went wrong . Please try later', error);
        });
    }
  };

  const resetSessionStates = () => {
    setIsEndedSession(false);
    setIsWaiting(true);
    setIsInitializing(true);
    setTelesessionToken(null);
    setTelesessionId(null);
    setSession(null);
  };

  const handleRestartSession = () => {
    patchAppointment(
      { action: PatchAction.RESTART_APPOINTMENT },
      { appointmentId: appointment.appointmentId },
    )
      .then(response => {
        if (response.status === 200) {
          resetSessionStates();
          joinAppointmentRequest(appointment?.appointmentId);
        }
      })
      .catch(err => {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: err?.data?.errors[0].endUserMessage,
          }),
        );
        console.log('Whoops ! something went wrong please try again', err);
      });
  };

  // Clean up when unmounting or navigating
  useEffect(() => {
    const cleanup = () => {
      if (session) {
        try {
          session.disconnect();
        } catch (e) {
          console.log('Error cleaning up session:', e);
        }
      }
    };

    // Handle history changes
    const unlisten = history.listen(() => {
      cleanup();
    });

    return () => {
      unlisten();
      cleanup();
    };
  }, [session]);

  return (
    <Box>
      {!isInitializing && appointment ? (
        <ProvideEvaluationHead>
          <VideoCallScreen
            isWaiting={isWaiting}
            setIsWaiting={setIsWaiting}
            onEndSession={toggleEndSessionConfirm}
            endSession={onEndSession}
            isEndedSession={isEndedSession}
            appointment={appointment}
            telesessionToken={telesessionToken}
            telesessionId={telesessionId}
            careTeamList={careTeamList}
            startInPersonSession={startInPersonCall}
            isInPersonSessionCall={inPersonSession}
            onRestartSessionClick={handleRestartSession}
            onMemberEndedSession={handleMemberEndedSession}
          />
        </ProvideEvaluationHead>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} height="100vh">
          <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
        </Box>
      )}

      <EndSession
        onClose={toggleEndSessionConfirm}
        onEndSession={onEndSession}
        open={showEndSessionConfirm}
        endedReason={callEndedReason}
      />
      <FeedbackAppointment
        appointmentId={appointmentId}
        open={showFeedbackAppointment}
        onClose={onCloseFeedbackAppointment}
      />
    </Box>
  );
};

export { Sessions };
